import { BigNum } from '@drift-labs/sdk';
import { TRADE_PRECISION } from '@drift/common';
import type {
	FormatNumberValue,
	FormatNumberOptions,
	FormatNumberMillifyOptions,
	FormatNumberPrecision,
} from 'src/utils/formatNumber/types';
import handleSpecialRendering from 'src/utils/handleSpecialRendering';
import Env from 'src/environmentVariables/EnvironmentVariables';

const NUMBER_FALLBACK = '-';
const SPECIAL_RENDERING_MODE = 'small';

export default function formatNumber(
	value: FormatNumberValue,
	opts: FormatNumberOptions
) {
	if (value == null) {
		return opts.fallback ?? NUMBER_FALLBACK;
	}

	const formattedNumberValue = formatNumberValue(
		value.abs(),
		opts,
		resolveDecimals(opts),
		getMillifyOptions(opts.millify)
	);

	const { start, end } = resolveSurround(opts);
	const { prefix, suffix } = resolveAffixes(value, opts);

	return `${start}${prefix}${formattedNumberValue}${suffix}${end}`;
}

function formatNumberValue(
	absValue: BigNum,
	opts: FormatNumberOptions,
	decimals: number | undefined,
	millifyOpts: FormatNumberMillifyOptions | undefined
) {
	const millifyEnabled =
		opts.millify === true || typeof opts.millify === 'object';
	const passesMillifyThreshold = shouldMillifyValue(absValue, millifyOpts);

	if (millifyEnabled && passesMillifyThreshold) {
		const millifiedValue = absValue.toMillified(
			resolveMillifyPrecision(absValue, millifyOpts),
			millifyOpts?.rounded
		);

		// We only return this value if it's actively being abbreviated (i.e. its num
		// digits warrants abbreviation). Otherwise, we let the function continue on
		// so that the value gets any passed in `decimals` or `precision` applied.
		if (isAbbreviatedMillifiedValue(millifiedValue)) {
			return millifiedValue;
		}
	}

	const precision = resolvePrecision(opts.precision);

	if (precision !== undefined) {
		const precisionValue = absValue.toPrecision(precision);

		return formatDisplayValue(
			opts.trimZeroes ? trimTrailingZeroes(precisionValue) : precisionValue
		);
	}

	if (decimals !== undefined) {
		return formatFixedValue(absValue, decimals, opts.trimZeroes ?? false);
	}

	if (opts.trimZeroes) {
		const shortValue = absValue.printShort();

		return formatDisplayValue(shortValue);
	}

	const rawValue = absValue.print();

	return formatDisplayValue(rawValue);
}

/**
 * We use `handleSpecialRendering` to handle potentially very small values
 * (adds unicode subscripts to the number, e.g. `0.0000123` -> `0.0₄123`).
 *
 * If the value is not small enough to warrant special rendering, we simply
 * return the value with commas added.
 */
function formatDisplayValue(value: string) {
	return handleSpecialRendering(value, {
		mode: SPECIAL_RENDERING_MODE,
		fallback: addCommas(value),
	});
}

function formatFixedValue(
	absValue: BigNum,
	decimals: number,
	trimZeroes: boolean
) {
	const fixedValue = absValue.toFixed(decimals);

	if (trimZeroes) {
		return formatDisplayValue(fixedValue.replace(/\.?0+$/, ''));
	}

	if (decimals <= 0) {
		return formatDisplayValue(fixedValue.replace(/\.$/, ''));
	}

	return formatDisplayValue(fixedValue);
}

/**
 * Returns the prefix and suffix for the given value and format type.
 */
function resolveAffixes(value: BigNum, opts: FormatNumberOptions) {
	const isNegative = value.ltZero();
	const gtZero = value.gtZero();
	const customSuffix = normalizeSuffix(opts.suffix);

	switch (opts.type) {
		case 'currency':
			return {
				prefix: isNegative ? '-$' : '$',
				suffix: customSuffix,
			};
		case 'currency_signed':
			return {
				prefix: isNegative ? '-$' : gtZero ? '+$' : '$',
				suffix: customSuffix,
			};
		case 'percentage':
			return {
				prefix: isNegative ? '-' : '',
				suffix: `%${customSuffix}`,
			};
		case 'percentage_signed':
			return {
				prefix: isNegative ? '-' : gtZero ? '+' : '',
				suffix: `%${customSuffix}`,
			};
		case 'number':
			return {
				prefix: isNegative ? '-' : '',
				suffix: customSuffix,
			};
		case 'number_signed':
			return {
				prefix: isNegative ? '-' : gtZero ? '+' : '',
				suffix: customSuffix,
			};
		default: {
			return {
				prefix: '',
				suffix: customSuffix,
			};
		}
	}
}

function normalizeSuffix(suffix: string | undefined) {
	if (!suffix) {
		return '';
	}

	const normalizedSuffix = suffix.trim();

	if (!normalizedSuffix) {
		return '';
	}

	return ` ${normalizedSuffix}`;
}

function resolveSurround(opts: FormatNumberOptions) {
	switch (opts.surround) {
		case 'parens':
			return { start: '(', end: ')' };
		default:
			return { start: '', end: '' };
	}
}

function trimTrailingZeroes(value: string) {
	const [mantissa, exponent] = value.split('e');
	const trimmedMantissa = mantissa.replace(/\.?0+$/, '');

	return exponent ? `${trimmedMantissa}e${exponent}` : trimmedMantissa;
}

function addCommas(value: string) {
	// Handle any exponential notation resulting from `BigNum.toPrecision`.
	if (/[eE]/.test(value)) {
		const numericValue = Number(value);

		if (Number.isFinite(numericValue)) {
			return numericValue.toLocaleString(Env.locale, {
				maximumFractionDigits: 20,
			});
		}
	}

	// At this point, if it was exponential we likely already early returned,
	// but we can still split on `e` just in case it was somehow infinite.
	const [mantissa] = value.split(/e/i);
	const [whole, decimal] = mantissa.split('.');
	const sign = whole.startsWith('-') ? '-' : whole.startsWith('+') ? '+' : '';
	const unsignedWhole = whole.replace(/^[-+]/, '').replace(/,/g, '');

	if (!unsignedWhole || !/^\d+$/.test(unsignedWhole)) {
		return value;
	}

	const wholeWithCommas = unsignedWhole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	const formattedMantissa = decimal
		? `${sign}${wholeWithCommas}.${decimal}`
		: `${sign}${wholeWithCommas}`;

	return formattedMantissa;
}

/**
 * Returns the number of decimals to display based on the passed options.
 * Defaults to `2` for currency and percentage types.
 */
function resolveDecimals(opts: FormatNumberOptions) {
	if (opts.decimals !== undefined) {
		const resolvedDecimals = resolveNonNegativeInteger(opts.decimals);

		if (resolvedDecimals !== undefined) {
			return resolvedDecimals;
		}
	}

	if (
		opts.type === 'currency' ||
		opts.type === 'currency_signed' ||
		opts.type === 'percentage' ||
		opts.type === 'percentage_signed'
	) {
		return 2;
	}
}

/**
 * Returns the significant digits to display based on the passed options.
 */
function resolvePrecision(precision: FormatNumberPrecision | undefined) {
	if (precision === 'trade') {
		return TRADE_PRECISION;
	}

	return resolvePositiveInteger(precision);
}

function resolveNonNegativeInteger(value: number | undefined) {
	if (value === undefined || !Number.isInteger(value)) {
		return;
	}

	return value >= 0 ? value : undefined;
}

function resolvePositiveInteger(value: number | undefined) {
	if (value === undefined || !Number.isInteger(value)) {
		return;
	}

	return value > 0 ? value : undefined;
}

function getMillifyOptions(
	millify: FormatNumberOptions['millify']
): FormatNumberMillifyOptions | undefined {
	return typeof millify === 'object' ? millify : undefined;
}

/**
 * Resolves the precision for `BigNum.toMillified` from the passed `millify.precision`
 * or `millify.decimals`, if any.
 *
 * If `millify.decimals` is passed, we need to convert it to a precision value,
 * as `BigNum.toMillified` only takes a precision argument.
 */
function resolveMillifyPrecision(
	absValue: BigNum,
	millifyOpts: FormatNumberMillifyOptions | undefined
) {
	const millifyDecimals = resolveNonNegativeInteger(millifyOpts?.decimals);

	if (millifyDecimals === undefined) {
		return resolvePrecision(millifyOpts?.precision);
	}

	// Number of digits in the original whole-number part (before any K/M/B abbreviation).
	const wholeDigitCount = gitMillifyWholeDigitCount(absValue);

	// How many digits will appear in the whole-number part after millifying:
	//   - <= 3 digits results in no change (e.g. 12, 123).
	//   - > 3 uses ((wholeDigitCount - 1) % 3) + 1, yielding 1..3 based on thousand groups.
	const integerMillifiedDigits =
		wholeDigitCount <= 3 ? wholeDigitCount : ((wholeDigitCount - 1) % 3) + 1;

	return Math.max(integerMillifiedDigits + millifyDecimals, 1);
}

function shouldMillifyValue(
	absValue: BigNum,
	millifyOpts: FormatNumberMillifyOptions | undefined
) {
	const threshold = resolveMillifyThreshold(millifyOpts?.threshold);

	if (threshold === undefined) {
		return true;
	}

	return absValue.toNum() >= threshold;
}

function resolveMillifyThreshold(value: number | undefined) {
	if (value === undefined || !Number.isFinite(value)) {
		return;
	}

	return value >= 0 ? value : undefined;
}

function isAbbreviatedMillifiedValue(value: string) {
	return /[KMBTQGPEZY]$/.test(value);
}

/**
 * Number of digits in the original whole-number part (before any K/M/B abbreviation).
 */
function gitMillifyWholeDigitCount(absValue: BigNum) {
	const wholeValue = absValue
		.print()
		.split('.')
		.at(0)
		?.replace(/^0+(?=\d)/, '');

	return Math.max(wholeValue?.length ?? 1, 1);
}
